import React, { useCallback, useContext, useState } from 'react';
import classNames from 'classnames';
import { Button } from 'ncoded-component-library';
import useUndoActions from 'hooks/useUndoActions';
import CurrentProfileContext from '../../../../providers/CurrentProfile/CurrentProfile.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { useTranslation } from 'react-i18next';
import { useFormState } from 'react-final-form';
import PopNotificationWithUndo from 'components/PopNotificationWithUndo';
import useResendVerificationMail from 'hooks/useResendVerificationMail';

import './ProfileActionsWithVerification.styles.scss';
import './ProfileActionsWithVerification.styles.responsive.scss';

type ProfileActionsWithVerificationProps = {
  className?: string;
};

const ProfileActionsWithVerification: React.FC<
  ProfileActionsWithVerificationProps
> = (props) => {
  const { className } = props;

  const classes = classNames('profile-actions-with-verification', className);

  const [isLoadingEmail, setIsLoadingEmail] = useState(false);
  const [isLoadingPhone, setIsLoadingPhone] = useState(false);

  const { isSuperAdmin } = useContext(CurrentUserContext);
  const { currentProfile, patchCurrentProfile } = useContext(
    CurrentProfileContext,
  );

  const { values, dirtyFields, errors } = useFormState();

  const { hasVerifiedEmail, hasVerifiedContactNumber } = currentProfile || {};

  const shouldShowVerifiedContactNumber =
    (!hasVerifiedContactNumber || dirtyFields.phone) &&
    values.phone?.phoneNumber &&
    !errors.phone;

  const shouldShowVerifiedEmail =
    (!hasVerifiedEmail || dirtyFields.email) && values.email && !errors.email;

  const shouldShowResendVerificationEmail =
    !hasVerifiedEmail && values.email && !dirtyFields.email;

  const { t } = useTranslation();

  const {
    isUndoAvailable: isUndoAvailableEmail,
    setUndoTimeout: setUndoTimeoutEmail,
    clearUndoTimeout: clearUndoTimeoutEmail,
  } = useUndoActions();

  const {
    isUndoAvailable: isUndoAvailablePhone,
    setUndoTimeout: setUndoTimeoutPhone,
    clearUndoTimeout: clearUndoTimeoutPhone,
  } = useUndoActions();

  const onVerifyEmail = useCallback(() => {
    setIsLoadingEmail(true);

    patchCurrentProfile(
      { email: values.email, hasVerifiedEmail: true },
      {
        skipToast: true,
        onSuccess: () => {
          setUndoTimeoutEmail();
          setIsLoadingEmail(false);
        },
        onError: () => {
          setIsLoadingEmail(false);
        },
      },
    );
  }, [patchCurrentProfile, setUndoTimeoutEmail, values.email]);

  const onUndoEmail = useCallback(() => {
    setIsLoadingEmail(true);

    patchCurrentProfile(
      { hasVerifiedEmail: false },
      {
        skipToast: true,
        onSuccess: () => {
          clearUndoTimeoutEmail();
          setIsLoadingEmail(false);
        },
        onError: () => {
          setIsLoadingEmail(false);
        },
      },
    );
  }, [clearUndoTimeoutEmail, patchCurrentProfile]);

  const onVerifyPhone = useCallback(() => {
    setIsLoadingPhone(true);

    patchCurrentProfile(
      { phone: values.phone, hasVerifiedContactNumber: true },
      {
        skipToast: true,
        onSuccess: () => {
          setUndoTimeoutPhone();
          setIsLoadingPhone(false);
        },
        onError: () => {
          setIsLoadingPhone(false);
        },
      },
    );
  }, [values.phone, patchCurrentProfile, setUndoTimeoutPhone]);

  const onUndoPhone = useCallback(() => {
    setIsLoadingPhone(true);

    patchCurrentProfile(
      { hasVerifiedContactNumber: false },
      {
        skipToast: true,
        onSuccess: () => {
          clearUndoTimeoutPhone();
          setIsLoadingPhone(false);
        },
        onError: () => {
          setIsLoadingPhone(false);
        },
      },
    );
  }, [clearUndoTimeoutPhone, patchCurrentProfile]);

  const { isLoading: isLoadingResendEmail, resendVerificationMail } =
    useResendVerificationMail();

  const hasAdminVerifications =
    isSuperAdmin &&
    (shouldShowVerifiedEmail || shouldShowVerifiedContactNumber);

  const hasResendVerification =
    !isSuperAdmin && shouldShowResendVerificationEmail;

  return (
    <>
      {(hasAdminVerifications || hasResendVerification) && (
        <div className={classes}>
          {isSuperAdmin ? (
            <div className="verifications">
              {shouldShowVerifiedEmail && (
                <Button disabled={isLoadingEmail} onClick={onVerifyEmail}>
                  {t('verifyEmail')}
                </Button>
              )}

              {shouldShowVerifiedContactNumber && (
                <Button disabled={isLoadingPhone} onClick={onVerifyPhone}>
                  {t('verifyPhoneNumber')}
                </Button>
              )}
            </div>
          ) : (
            <div className="verifications">
              <Button
                variant="outline"
                disabled={isLoadingResendEmail}
                onClick={() => resendVerificationMail()}
              >
                {t('resendVerificationEmail')}
              </Button>
            </div>
          )}
        </div>
      )}

      <PopNotificationWithUndo
        isUndoAvailable={isUndoAvailableEmail}
        disabled={isLoadingEmail}
        onUndo={onUndoEmail}
      >
        <p>{t('emailIsVerified')}</p>
      </PopNotificationWithUndo>

      <PopNotificationWithUndo
        isUndoAvailable={isUndoAvailablePhone}
        disabled={isLoadingPhone}
        onUndo={onUndoPhone}
      >
        <p>{t('phoneNumberIsVerified')}</p>
      </PopNotificationWithUndo>
    </>
  );
};

export default ProfileActionsWithVerification;
